import React, { useState } from "react";
import {
  Box, Flex, Text, Button, Badge, HStack, Switch, Select, Input,
  FormControl, FormLabel, Alert, AlertIcon, Collapse, IconButton, Tooltip, SimpleGrid, Divider,
} from "@chakra-ui/react";
import { Settings, ChevronDown, ChevronUp } from "lucide-react";
import { salvarConfigNotaDevolucao } from "../../services/api";
import ConfirmDialog from "../ConfirmDialog";

const ACCENT = "#F68633";

/**
 * Configuração da emissão (#2225).
 *
 * Fica na tela da NF-e de devolução do cliente, e não em /configuracoes, porque
 * ligar emissão de nota fiscal não
 * pode ser um campo numérico perdido no meio das alíquotas. Trocar homologação
 * por produção pede confirmação explícita: em produção a nota vale, e nota
 * autorizada não se desfaz.
 */
export default function ConfigNotaDevolucao({ config, onSalvo, toast }) {
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState({
    ativo: !!config?.ativo,
    ambiente: config?.ambiente || "homologacao",
    delay_minutos: config?.delay_minutos ?? 5,
    data_icms_normal: config?.data_icms_normal || "2026-11-01",
    data_presumido: config?.data_presumido || "2027-01-01",
    data_icms_normal_hmg: config?.data_icms_normal_hmg || config?.data_icms_normal || "2026-11-01",
    data_presumido_hmg: config?.data_presumido_hmg || config?.data_presumido || "2027-01-01",
    csosn: config?.csosn || "900",
    cst_pis_cofins: config?.cst_pis_cofins || "99",
    prazo_credito_dias: config?.prazo_credito_dias ?? 45,
    ibscbs_sem_origem: config?.ibscbs_sem_origem || "OMITIR",
    ibscbs_cst: config?.ibscbs_cst || "",
    ibscbs_cclasstrib: config?.ibscbs_cclasstrib || "",
  });
  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const [salvando, setSalvando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const emProducao = config?.ambiente === "producao";
  const ligado = !!config?.ativo;

  // Só pede confirmação quando a mudança faz nota passar a valer de verdade.
  const viraProducaoReal = form.ambiente === "producao" && form.ativo && !(emProducao && ligado);

  const persistir = async () => {
    setSalvando(true);
    const res = await salvarConfigNotaDevolucao({
      ativo: form.ativo,
      ambiente: form.ambiente,
      delay_minutos: Number(form.delay_minutos),
      data_icms_normal: form.data_icms_normal,
      data_presumido: form.data_presumido,
      data_icms_normal_hmg: form.data_icms_normal_hmg,
      data_presumido_hmg: form.data_presumido_hmg,
      csosn: form.csosn,
      cst_pis_cofins: form.cst_pis_cofins,
      prazo_credito_dias: Number(form.prazo_credito_dias),
      ibscbs_sem_origem: form.ibscbs_sem_origem,
      ibscbs_cst: form.ibscbs_cst || null,
      ibscbs_cclasstrib: form.ibscbs_cclasstrib || null,
    });
    setSalvando(false);
    setConfirmando(false);

    if (res?.error) {
      toast?.({ title: "Não foi possível salvar", description: res.error, status: "error", duration: 6000 });
      return;
    }

    toast?.({
      title: "Configuração salva",
      description: res.ativo
        ? `Emissão ligada em ${res.ambiente === "producao" ? "PRODUÇÃO" : "homologação"}.`
        : "Emissão desligada.",
      status: "success",
      duration: 5000,
    });
    onSalvo?.(res);
  };

  const salvar = () => (viraProducaoReal ? setConfirmando(true) : persistir());

  return (
    <Box bg="white" borderWidth="1px" borderRadius="lg" mb={4}
      borderColor={ligado && emProducao ? "green.300" : ligado ? "orange.300" : "gray.200"}>
      <Flex px={4} py={3} justify="space-between" align="center" gap={3} wrap="wrap"
        cursor="pointer" onClick={() => setAberto((v) => !v)}>
        <HStack spacing={3} minW={0}>
          <Box color={ACCENT}><Settings size={18} /></Box>
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="semibold">Emissão automática</Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {ligado
                ? `Ligada · ${config.delay_minutos} min depois de marcar como recebida`
                : "Desligada · marcar como recebida não emite nota"}
            </Text>
          </Box>
        </HStack>

        <HStack spacing={2}>
          <Badge colorScheme={ligado ? "green" : "gray"} fontSize="10px" px={2} borderRadius="md">
            {ligado ? "LIGADA" : "DESLIGADA"}
          </Badge>
          <Badge colorScheme={emProducao ? "red" : "purple"} fontSize="10px" px={2} borderRadius="md">
            {emProducao ? "PRODUÇÃO" : "HOMOLOGAÇÃO"}
          </Badge>
          <IconButton size="xs" variant="ghost" aria-label="Abrir"
            icon={aberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />} />
        </HStack>
      </Flex>

      <Collapse in={aberto} animateOpacity>
        <Box px={4} pb={4} borderTopWidth="1px">
          <Alert status={form.ambiente === "producao" ? "warning" : "info"} borderRadius="md" mt={3} fontSize="xs">
            <AlertIcon boxSize="14px" />
            {form.ambiente === "producao"
              ? "Produção emite NF-e com valor fiscal. Nota autorizada não se desfaz: só cancelamento dentro do prazo legal."
              : "Homologação é ensaio na SEFAZ de teste. A nota não tem valor fiscal e serve para conferir o XML."}
          </Alert>

          <Flex gap={4} mt={4} wrap="wrap" align="flex-end">
            <FormControl w={{ base: "100%", sm: "180px" }}>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>Emitir no recebimento</FormLabel>
              <HStack>
                <Switch colorScheme="orange" isChecked={form.ativo}
                  onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))} />
                <Text fontSize="sm">{form.ativo ? "Sim" : "Não"}</Text>
              </HStack>
            </FormControl>

            <FormControl w={{ base: "100%", sm: "220px" }}>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>Ambiente</FormLabel>
              <Select size="sm" value={form.ambiente}
                onChange={(e) => setForm((f) => ({ ...f, ambiente: e.target.value }))}>
                <option value="homologacao">Homologação (sem valor fiscal)</option>
                <option value="producao">Produção (nota vale)</option>
              </Select>
            </FormControl>

            <FormControl w={{ base: "100%", sm: "200px" }}>
              <Tooltip label="Tempo entre marcar como recebida e a nota sair. Serve para dar tempo de desfazer." hasArrow>
                <FormLabel fontSize="xs" color="gray.600" mb={1}>Atraso (minutos)</FormLabel>
              </Tooltip>
              <Input size="sm" type="number" min={0} max={120} value={form.delay_minutos}
                onChange={(e) => setForm((f) => ({ ...f, delay_minutos: e.target.value }))} />
            </FormControl>

          </Flex>

          <Divider my={4} />
          <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={3}>Parâmetros fiscais</Text>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>ICMS normal (CRT 2) a partir de</FormLabel>
              <Input size="sm" type="date" value={form.data_icms_normal} onChange={set("data_icms_normal")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>Lucro Presumido (CRT 3) a partir de</FormLabel>
              <Input size="sm" type="date" value={form.data_presumido} onChange={set("data_presumido")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>CRT 2 em homologação a partir de</FormLabel>
              <Input size="sm" type="date" value={form.data_icms_normal_hmg} onChange={set("data_icms_normal_hmg")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>CRT 3 em homologação a partir de</FormLabel>
              <Input size="sm" type="date" value={form.data_presumido_hmg} onChange={set("data_presumido_hmg")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>CSOSN (CRT 1)</FormLabel>
              <Select size="sm" value={form.csosn} onChange={set("csosn")}>
                {["900", "102", "103", "300", "400"].map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>CST PIS/COFINS</FormLabel>
              <Input size="sm" maxLength={2} value={form.cst_pis_cofins} onChange={set("cst_pis_cofins")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>Prazo do crédito de ICMS (dias)</FormLabel>
              <Input size="sm" type="number" min={0} value={form.prazo_credito_dias} onChange={set("prazo_credito_dias")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>IBS/CBS sem origem na venda</FormLabel>
              <Select size="sm" value={form.ibscbs_sem_origem} onChange={set("ibscbs_sem_origem")}>
                <option value="OMITIR">OMITIR</option>
                <option value="ZERADO">ZERADO</option>
              </Select>
            </FormControl>
            {form.ibscbs_sem_origem === "ZERADO" && (
              <>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.600" mb={1}>CST do IBS/CBS</FormLabel>
                  <Input size="sm" maxLength={3} value={form.ibscbs_cst} onChange={set("ibscbs_cst")} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.600" mb={1}>cClassTrib</FormLabel>
                  <Input size="sm" maxLength={6} value={form.ibscbs_cclasstrib} onChange={set("ibscbs_cclasstrib")} />
                </FormControl>
              </>
            )}
          </SimpleGrid>

          <Flex justify="flex-end" mt={4}>
            <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
              isLoading={salvando} onClick={salvar} w={{ base: "100%", sm: "auto" }}>
              Salvar
            </Button>
          </Flex>
        </Box>
      </Collapse>

      <ConfirmDialog
        isOpen={confirmando}
        onClose={() => setConfirmando(false)}
        onConfirm={persistir}
        title="Ligar emissão em PRODUÇÃO?"
        body={
          "A partir de agora, marcar uma devolução como recebida vai emitir NF-e com valor fiscal, " +
          `${form.delay_minutos} minuto(s) depois do clique. Nota autorizada não se desfaz: só cancelamento ` +
          "dentro do prazo legal. Confirma?"
        }
        confirmText="Sim, emitir valendo"
        cancelText="Cancelar"
        confirmColor="red"
        isLoading={salvando}
      />
    </Box>
  );
}
